import React, { useState, useEffect } from "react";
import { Grid, Paper, Box, Typography, CircularProgress } from "@mui/material";
import ServiceTabs from "../NewServices/Tabs";
import GenericUtilityService from "./GenericUtilityService";
import MobilePrepaidRecharge from "./MobilePrepaidRecharge";
import { useLocation } from "react-router-dom";

export default function RetailerServices() {
  const location = useLocation();
  const { label, categoryKey: initialCategoryKey } = location.state || {};
  const [selectedService, setSelectedService] = useState(label || "");
  const [billers, setBillers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [categoryKey, setCategoryKey] = useState(initialCategoryKey || "");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMoreBillers, setHasMoreBillers] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError("");

      const jwtToken = sessionStorage.getItem("jwtToken");
      if (!jwtToken) {
        throw new Error("JWT token not found in sessionStorage");
      }

      const response = await fetch(
        "https://api.ipaisa.co.in/myapp/billPayments/getAllCategory",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      const responseData = await response.json();
      
      // Validate response structure
      if (!responseData.body || !responseData.body.data || !Array.isArray(responseData.body.data)) {
        throw new Error("Invalid response structure from API");
      }

      const sortedCategories = responseData.body.data.sort((a, b) => {
        const numA = parseInt(a.categoryKey?.substring(1) || "0"); // Safe parsing with fallback
        const numB = parseInt(b.categoryKey?.substring(1) || "0"); // Safe parsing with fallback
        return numA - numB;
      });
  
      setCategories(sortedCategories);

      if (initialCategoryKey || label) {
        const initialCategory = responseData.body.data.find(
          (category) =>
            category.categoryKey === initialCategoryKey ||
            category.categoryName === label
        );
        if (initialCategory) {
          setSelectedCategory(initialCategory);
          setSelectedService(initialCategory.categoryName);
          setCategoryKey(initialCategory.categoryKey);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to fetch categories");
      setCategories([]); // Reset categories on error
    } finally {
      setLoading(false);
    }
  };

  const fetchBillersByCategory = async (key, page = 0, searchBy = "") => {
    try {
      if (!hasMoreBillers && page !== 0 && !searchBy) return;
      
      setLoading(true);
      setError("");

      const jwtToken = sessionStorage.getItem("jwtToken");
      if (!jwtToken) {
        throw new Error("JWT token not found in sessionStorage");
      }

      let apiUrl = `https://api.ipaisa.co.in/myapp/billPayments/getBillersBycategory?categoryKey=${key}&page=${page}&size=50`;
      if (searchBy) {
        apiUrl += `&searchBy=${encodeURIComponent(searchBy)}`;
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch billers: ${response.statusText}`);
      }

      const responseData = await response.json();
      
      // Validate response structure
      if (!responseData.body || !Array.isArray(responseData.body.data)) {
        throw new Error("Invalid billers data structure from API");
      }

      setTotalPages(responseData.body.totalPages || 0);
      setHasMoreBillers(page < (responseData.body.totalPages || 1) - 1);

      if (page === 0 || searchBy) {
        // Reset or search results
        setBillers(responseData.body.data || []);
      } else {
        // Pagination results
        setBillers((prevBillers) => [...prevBillers, ...(responseData.body.data || [])]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch billers");
      setBillers([]); // Reset billers on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (categoryKey) {
      const timer = setTimeout(() => {
        fetchBillersByCategory(categoryKey, 0, searchTerm);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [categoryKey, searchTerm]);

  useEffect(() => {
    if (categoryKey && currentPage > 0 && !searchTerm) {
      fetchBillersByCategory(categoryKey, currentPage);
    }
  }, [categoryKey, currentPage, searchTerm]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedService(category.categoryName);
    setCategoryKey(category.categoryKey);
    setCurrentPage(0);
    setSearchTerm("");
    setHasMoreBillers(true);
  };

  const loadMoreBillers = () => {
    if (!loading && hasMoreBillers && !searchTerm) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(2% + 58px)" }}>
      <Grid
        style={{
          marginTop: "1%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          style={{
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "19px",
            color: "#343C6A",
          }}
        >
          Utility BillPayments
        </Typography>
      </Grid>

      <Paper
        style={{
          boxShadow: "none",
          borderRadius: "18px 18px 18px 18px",
          marginTop: "2%",
        }}
      >
        <Grid>
          <ServiceTabs
            setSelectedService={setSelectedService}
            setCategoryKey={setCategoryKey}
            selectedService={selectedService}
            selectedCategory={categoryKey}
            categories={categories}
            onCategoryChange={handleCategoryChange}
          />
          {loading && currentPage === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : selectedCategory?.categoryKey === "C00" ? (
            <MobilePrepaidRecharge />
          ) : (
            <GenericUtilityService
              billers={billers}
              serviceName={selectedService}
              loadMoreBillers={loadMoreBillers}
              hasMoreBillers={hasMoreBillers}
              isLoading={loading}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          )}
        </Grid>
      </Paper>
    </Box>
  );
}